/**
 * Broker SearchScrip row-matching — Phase 6a-9 commit 23 of the decomposition
 * (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, v2.21/R35). Bodies moved
 * VERBATIM from static/live.html; the page aliases them at the original
 * source positions.
 *
 * WHY THIS EXISTS (order-path fail-safe): the live terminal matches
 * Flattrade's REAL SearchScrip rows by attributes and NEVER hand-builds the
 * tradingsymbol it orders. NSE and BSE encode differently
 * (NSE: ROOT+DD+MON+YY+C|P+STRIKE, e.g. NIFTY04AUG26P23800;
 *  BSE: ROOT+YY+MON+STRIKE+CE|PE, e.g. SENSEX26JUL77600CE), both with
 * weekly/monthly variants. A row is accepted only when strike, option-type
 * AND expiry all positively match — anything unreadable is a NO-match (fail
 * safe: never order a contract we couldn't positively confirm).
 *
 * Date sourcing rule: the HUMAN date fields (exd/expd/exp/exchdt, then dname
 * — "Sensex 30 Jul 26 …") carry the true expiry even where the tsym uses a
 * compact/monthly encoding. NEVER parse the tsym for the date (BSE puts the
 * YEAR there, not the day). null => couldn't read it -> treat as no-match.
 *
 * Pure module: explicit inputs only — zero DOM, timers, clocks, page state,
 * or callbacks. The Date argument to rowMatchesContract is caller-built and
 * read with local getters (getDate/getMonth/getFullYear), so results are
 * deterministic given the arguments.
 */

import { fyersMonthChar } from "../symbols";

export const OPT_MON = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
export const OPT_MON_IDX: Record<string, number> = Object.fromEntries(OPT_MON.map((m, i) => [m, i]));

/** One raw Flattrade SearchScrip row — only the fields this matcher reads. */
export interface BrokerRow {
  tsym?: string;
  optt?: string;
  strprc?: string | number | null;
  exd?: string; expd?: string; exp?: string; exchdt?: string; dname?: string;
  [key: string]: unknown;
}

export interface ExpiryParts { day: number; mon: number; year: number | null; }

/** (day,mon[,year]) from a row's HUMAN date fields; null = unreadable (safe no-match). */
export function rowExpiryParts(v: BrokerRow | null | undefined): ExpiryParts | null {
  for (const key of ['exd', 'expd', 'exp', 'exchdt', 'dname']) {
    const s = v && (v as Record<string, unknown>)[key]; if (!s) continue;
    const m = String(s).toUpperCase().match(/(\d{1,2})[ \-\/]?([A-Z]{3})[ \-\/]?(\d{2,4})?/);
    if (m && OPT_MON_IDX[m[2]] != null) {
      let y = m[3] != null && m[3] !== '' ? parseInt(m[3]) : null;
      if (y != null && y < 100) y += 2000;
      return { day: parseInt(m[1]), mon: OPT_MON_IDX[m[2]], year: y };
    }
  }
  return null;
}

export function rowTypeMatches(v: BrokerRow, side: 'CE' | 'PE'): boolean {
  const t = String(v.optt || '').toUpperCase();
  if (t === 'CE' || t === 'PE') return t === side;
  const ts = String(v.tsym || '').toUpperCase();
  if (ts.endsWith('CE') || ts.endsWith('PE')) return ts.endsWith(side);        // BSE
  const m = ts.match(/\d{2}[A-Z]{3}\d{2}([CP])\d+$/);                            // NSE
  return m ? m[1] === (side === 'CE' ? 'C' : 'P') : false;
}

/**
 * BSE/BFO tradingsymbol prefixes for a given expiry — the part BEFORE the strike.
 * Encodings confirmed from live SearchScrip captures (2026-08-06 August, 2026-09-30 October/November):
 *   weekly  ROOT + YY + M + DD + STRIKE + CE|PE   SENSEX 26 8 06 69100 CE (Aug), SENSEX 26 O 01 65500 CE (Oct)
 *   monthly ROOT + YY + MON  + STRIKE + CE|PE     SENSEX 26 AUG 78800 CE, SENSEX 26 OCT 65500 CE
 * The weekly month character is a single alphanumeric char: 1-9 for Jan-Sep, then O/N/D (fyersMonthChar).
 */
function bsePrefixesFor(root: string, d: Date): string[] {
  const yy = String(d.getFullYear()).slice(2);
  const mon = OPT_MON[d.getMonth()];
  const mChar = fyersMonthChar(d.getMonth());
  const dd = String(d.getDate()).padStart(2, '0');
  const prefixes = [`${root}${yy}${mon}`];
  if (mChar) prefixes.unshift(`${root}${yy}${mChar}${dd}`);
  return prefixes;
}

export function rowStrikeMatches(v: BrokerRow, strike: string | number, d?: Date): boolean {
  const want = parseFloat(String(strike));
  if (v.strprc != null && v.strprc !== '') { const s = parseFloat(String(v.strprc)); if (!isNaN(s)) return s === want; }
  const ts = String(v.tsym || '').toUpperCase();

  // ── BSE/BFO: trailing CE|PE. strprc is absent on EVERY BFO row (confirmed
  // live), so this fallback is always the deciding path here — it must be exact.
  //
  // The old regex read the digit run before CE|PE greedily. That works only when
  // a LETTER delimits the strike (monthly …AUG78800CE -> 78800), and silently
  // mis-read every WEEKLY, which has no delimiter: SENSEX2680669100CE captured
  // 2680669100 instead of 69100, so a correct weekly row was rejected as a strike
  // mismatch. Widening the regex can't fix that — 2680669100 is genuinely
  // ambiguous in isolation. Instead, use the REQUESTED expiry to derive the exact
  // prefix and strip it; whatever remains is the strike, unambiguously.
  if (/(?:CE|PE)$/.test(ts)) {
    const body = ts.slice(0, -2);
    const rootMatch = body.match(/^([A-Z]+)/);
    if (!rootMatch) return false;
    if (d) {
      for (const prefix of bsePrefixesFor(rootMatch[1], d)) {
        if (!body.startsWith(prefix)) continue;
        const rest = body.slice(prefix.length);
        if (/^\d+$/.test(rest)) return parseFloat(rest) === want;
      }
      return false;   // matches NEITHER derived encoding -> fail closed, never guess
    }
    // No expiry context: only safe where a letter delimits the strike (monthly).
    // A delimiter-less weekly is ambiguous without the date, so it fails closed.
    const delimited = body.match(/[A-Z](\d+)$/);
    return delimited ? parseFloat(delimited[1]) === want : false;
  }

  const nse = ts.match(/[CP](\d+)$/);                 // NSE: digits after C|P
  return nse ? parseFloat(nse[1]) === want : false;
}

/**
 * Verdict for "does the execution broker list ANY contract for this expiry?"
 *
 * The live terminal's expiry dropdown comes from UPSTOX (chain data) but orders
 * are placed at FLATTRADE, so the UI could offer an expiry the execution venue
 * doesn't have — live 2026-08-06: SENSEX offered 06-AUG-2026 while Flattrade
 * listed only 27-AUG-2026, so nothing could ever resolve and the terminal showed
 * no price. This grades one probe search's rows against the wanted date.
 *
 * THREE-valued on purpose, and the caller must FAIL OPEN on 'inconclusive':
 *   'tradeable'    — at least one row positively carries that expiry.
 *   'absent'       — rows came back and were READABLE, and none carried it.
 *   'inconclusive' — no rows (broker "No Data"/error) or nothing readable.
 * Only 'absent' is evidence. Hiding a real expiry silently blocks trading, which
 * is worse than offering one that fails loudly at resolve time. Never widen this
 * to gate ORDER resolution — rowMatchesContract stays the fail-closed authority.
 */
export type ExpiryProbeVerdict = 'tradeable' | 'absent' | 'inconclusive';

/** SearchScrip caps its result set — a full page proves nothing about absence. */
export const SEARCH_SCRIP_ROW_CAP = 25;

export function expiryProbeVerdict(
  rows: BrokerRow[] | null | undefined, d: Date, cap: number = SEARCH_SCRIP_ROW_CAP,
): ExpiryProbeVerdict {
  if (!rows || !rows.length) return 'inconclusive';
  let readable = 0;
  for (const v of rows) {
    const p = rowExpiryParts(v);
    if (!p) continue;                                    // unreadable row — not evidence
    readable++;
    if (p.day === d.getDate() && p.mon === d.getMonth() &&
        (p.year == null || p.year === d.getFullYear())) return 'tradeable';
  }
  // A CAPPED result set is not evidence of absence: SearchScrip truncates at 25
  // (confirmed live — SENSEX26AUG returned exactly 25 monthly rows), so the wanted
  // expiry can simply have been cut off. Only an UNDER-CAP, readable, non-matching
  // page can say 'absent'.
  if (rows.length >= cap) return 'inconclusive';
  return readable ? 'absent' : 'inconclusive';
}

export function rowMatchesContract(v: BrokerRow, side: 'CE' | 'PE', strike: string | number, d: Date): boolean {
  // Pass the requested expiry: BSE strikes can only be read unambiguously by
  // stripping the expiry-derived prefix (see rowStrikeMatches).
  if (!rowTypeMatches(v, side) || !rowStrikeMatches(v, strike, d)) return false;
  const p = rowExpiryParts(v);
  if (!p) return false;                                                          // can't confirm expiry -> reject
  if (p.day !== d.getDate() || p.mon !== d.getMonth()) return false;
  if (p.year != null && p.year !== d.getFullYear()) return false;
  return true;
}

/**
 * Generates SearchScrip probe queries for an expiry date across NSE and BSE conventions.
 */
export function expiryProbeTexts(
  root: string,
  exch: string,
  d: Date,
): string[] {
  const mon = OPT_MON[d.getMonth()];
  const yy = String(d.getFullYear()).slice(2);
  const dd = String(d.getDate()).padStart(2, "0");
  const mChar = fyersMonthChar(d.getMonth());
  return exch === "BFO" || exch === "BSE"
    ? (mChar ? [`${root}${yy}${mChar}${dd}`, `${root}${yy}${mon}`] : [`${root}${yy}${mon}`])
    : [`${root}${dd}${mon}${yy}`];
}

export interface ChainStrikeRow {
  strike: number | string;
  ce_symbol?: string;
  pe_symbol?: string;
  [key: string]: unknown;
}

/**
 * Maps a broker tradingsymbol to its streaming feed symbol (fysym) from the loaded option chain strikes.
 * Handles both NSE and BSE conventions, monthly and weekly.
 */
export function resolveFysymFromStrikes(
  tsym: string | null | undefined,
  root: string,
  exch: string,
  d: Date,
  strikes: ChainStrikeRow[] | null | undefined,
): string | null {
  if (!tsym || !root || !exch || !d || !strikes || !strikes.length) return null;
  const ts = String(tsym).toUpperCase();
  if (!ts.startsWith(root)) return null;              // only positions of the loaded index
  const mon = OPT_MON[d.getMonth()];
  const DD = String(d.getDate()).padStart(2, "0");
  const YY = String(d.getFullYear()).slice(2);
  const isBse = exch === "BFO" || exch === "BSE";
  const mChar = fyersMonthChar(d.getMonth());

  for (const x of strikes) {
    const s = String(x.strike);
    if (isBse) {
      if (ts === `${root}${YY}${mon}${s}CE`) return x.ce_symbol || null;
      if (ts === `${root}${YY}${mon}${s}PE`) return x.pe_symbol || null;
      if (mChar) {
        if (ts === `${root}${YY}${mChar}${DD}${s}CE`) return x.ce_symbol || null;
        if (ts === `${root}${YY}${mChar}${DD}${s}PE`) return x.pe_symbol || null;
      }
    } else {
      if (ts === `${root}${DD}${mon}${YY}C${s}`) return x.ce_symbol || null;
      if (ts === `${root}${DD}${mon}${YY}P${s}`) return x.pe_symbol || null;
    }
  }
  return null;
}

